// Documents module (Phase 3 Module 3) — parent + admin helpers for
// family-scope and child-scope documents.
//
// Reuses the document_review_status enum and statusTone()/summariseChecklist()
// patterns from lib/documentChecklist.js (enrollment scope), but handles the
// new concepts:
//   * scope (family | child) — which table to read from
//   * requires_signature — row created by rpc_sign_document
//   * expires_at — rendered as a warning chip when approaching / past expiry
//
// Returned row shape is intentionally identical across scopes so a single
// UI widget can render them.

import { supabase, isSupabaseConfigured } from "./supabase";

function assertLive() {
  if (!isSupabaseConfigured) throw new Error("Documents module requires live Supabase.");
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export async function listDocumentTypes({ organizationId, scope }) {
  assertLive();
  if (!organizationId) return [];
  let q = supabase
    .from("document_type_definitions")
    .select(
      "id, organization_id, scope, code, label, description, is_required, sort_order, requires_signature, validity_months",
    )
    .eq("organization_id", organizationId)
    .order("sort_order", { ascending: true });
  if (scope) q = q.eq("scope", scope);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

// ---------------------------------------------------------------------------
// Family documents
// ---------------------------------------------------------------------------

const FAMILY_DOC_COLS =
  "id, family_id, organization_id, document_type_id, storage_path, uploaded_by, status, reviewed_by, reviewed_at, review_note, signed_at, signed_name, signed_by, signed_ip_address, expires_at, created_at";

export async function listFamilyDocuments(familyId) {
  assertLive();
  const { data, error } = await supabase
    .from("family_documents")
    .select(FAMILY_DOC_COLS)
    .eq("family_id", familyId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function deleteFamilyDocument({ id, storagePath }) {
  assertLive();
  const { error } = await supabase.from("family_documents").delete().eq("id", id);
  if (error) throw error;
  if (storagePath) {
    await supabase.storage
      .from("family-documents")
      .remove([storagePath])
      .catch(() => {});
  }
}

// ---------------------------------------------------------------------------
// Child documents
// ---------------------------------------------------------------------------

const CHILD_DOC_COLS =
  "id, child_id, organization_id, document_type_id, storage_path, uploaded_by, status, reviewed_by, reviewed_at, review_note, signed_at, signed_name, signed_by, signed_ip_address, expires_at, created_at";

export async function listChildDocuments(childId) {
  assertLive();
  const { data, error } = await supabase
    .from("child_documents")
    .select(CHILD_DOC_COLS)
    .eq("child_id", childId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function deleteChildDocument({ id, storagePath }) {
  assertLive();
  const { error } = await supabase.from("child_documents").delete().eq("id", id);
  if (error) throw error;
  if (storagePath) {
    await supabase.storage
      .from("child-documents")
      .remove([storagePath])
      .catch(() => {});
  }
}

// ---------------------------------------------------------------------------
// E-signature
// ---------------------------------------------------------------------------

export async function signDocument({
  scope, // 'family' | 'child'
  recordId, // family_id or child_id
  documentTypeId,
  signedName,
}) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_sign_document", {
    p_scope: scope,
    p_record_id: recordId,
    p_document_type_id: documentTypeId,
    p_signed_name: signedName,
    p_ip_address: null, // server could populate via x-forwarded-for in future
  });
  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// Admin review (unified across scopes)
// ---------------------------------------------------------------------------

export async function reviewDocument({ scope, documentId, status, note }) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_review_document", {
    p_scope: scope,
    p_document_id: documentId,
    p_status: status,
    p_review_note: note || null,
  });
  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// Checklist derivation — one row per required type + extras grouped.
// ---------------------------------------------------------------------------

/**
 * Combine a list of types and the docs under a record into checklist rows.
 * Each row has: { type, documents, latest, effectiveStatus, expiryState }
 * effectiveStatus: 'missing' | 'uploaded' | 'approved' | 'rejected'
 * expiryState:     'ok' | 'expiring' | 'expired' | null  (only meaningful if latest exists)
 */
export function buildDocumentChecklistRows(types, docs) {
  const byType = {};
  for (const d of docs || []) {
    const key = d.document_type_id || "__other__";
    (byType[key] ||= []).push(d);
  }
  const rows = (types || []).map((t) => {
    const list = byType[t.id] || [];
    const latest = pickLatest(list);
    return {
      type: t,
      documents: list,
      latest,
      effectiveStatus: latest ? latest.status || "uploaded" : "missing",
      expiryState: computeExpiryState(latest),
    };
  });
  // Capture any orphan uploads (type deleted, or type mismatch) under a
  // single synthetic row so the UI never renders raw UUIDs.
  const others = byType.__other__ || [];
  if (others.length > 0) {
    const latest = pickLatest(others);
    rows.push({
      type: {
        id: "__other__",
        code: "other_uploads",
        label: "Other uploads",
        description: "Documents not tied to a current requirement.",
        is_required: false,
        sort_order: 9999,
        requires_signature: false,
        validity_months: 0,
      },
      documents: others,
      latest,
      effectiveStatus: latest ? latest.status || "uploaded" : "missing",
      expiryState: computeExpiryState(latest),
    });
  }
  return rows;
}

function pickLatest(list) {
  if (!list || list.length === 0) return null;
  return [...list].sort((a, b) =>
    (b.created_at || "").localeCompare(a.created_at || ""),
  )[0];
}

function computeExpiryState(doc) {
  if (!doc || !doc.expires_at) return null;
  const now = Date.now();
  const exp = new Date(doc.expires_at).getTime();
  if (Number.isNaN(exp)) return null;
  const thirtyDays = 30 * 24 * 60 * 60 * 1000;
  if (exp < now) return "expired";
  if (exp - now < thirtyDays) return "expiring";
  return "ok";
}

/**
 * Summary numbers for the readiness chip.
 */
export function summariseDocumentChecklist(rows) {
  const req = rows.filter((r) => r.type?.is_required);
  const approved = req.filter((r) => r.effectiveStatus === "approved").length;
  const uploaded = req.filter((r) => r.effectiveStatus === "uploaded").length;
  const rejected = req.filter((r) => r.effectiveStatus === "rejected").length;
  const missing = req.filter((r) => r.effectiveStatus === "missing").length;
  const expired = req.filter((r) => r.expiryState === "expired").length;
  const expiring = req.filter((r) => r.expiryState === "expiring").length;
  return {
    totalRequired: req.length,
    approvedCount: approved,
    uploadedCount: uploaded,
    rejectedCount: rejected,
    missingRequired: missing,
    expiredRequired: expired,
    expiringRequired: expiring,
    percentComplete:
      req.length === 0
        ? 100
        : Math.round(((approved + uploaded) / req.length) * 100),
  };
}

// ---------------------------------------------------------------------------
// Status + expiry tone tokens
// ---------------------------------------------------------------------------

export function documentStatusTone(status) {
  switch (status) {
    case "approved":
      return { bg: "bg-primary/10", color: "text-primary-hover", ring: "border-primary/30", label: "Approved" };
    case "uploaded":
      return { bg: "bg-secondary/30", color: "text-neutral-900", ring: "border-secondary/50", label: "Pending review" };
    case "rejected":
      return { bg: "bg-[#C6851E]/10", color: "text-[#C6851E]", ring: "border-[#C6851E]/40", label: "Rejected" };
    case "missing":
      return { bg: "bg-neutral-100", color: "text-neutral-500", ring: "border-neutral-200", label: "Missing" };
    default:
      return { bg: "bg-neutral-100", color: "text-neutral-600", ring: "border-neutral-200", label: status || "—" };
  }
}

export function expiryTone(state) {
  switch (state) {
    case "expired":
      return { bg: "bg-[#C6851E]/10", color: "text-[#C6851E]", ring: "border-[#C6851E]/40", label: "Expired" };
    case "expiring":
      return { bg: "bg-amber-50", color: "text-amber-700", ring: "border-amber-200", label: "Expiring soon" };
    case "ok":
      return { bg: "bg-primary/10", color: "text-primary-hover", ring: "border-primary/30", label: "Current" };
    default:
      return null;
  }
}

export function formatExpiry(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Parent-side aggregation: single-shot summary across family + every child
// doc. Designed for the dashboard badge — avoid duplicating the full-page
// fetch there.
// ---------------------------------------------------------------------------

export async function getFamilyComplianceSummary(profileId) {
  assertLive();
  // 1. Resolve the parent's family + children in two light queries.
  const { data: memberships, error: mErr } = await supabase
    .from("family_members")
    .select("family_id, families:families!inner(id, organization_id)")
    .eq("profile_id", profileId)
    .limit(1);
  if (mErr) throw mErr;
  const fam = memberships?.[0]?.families;
  if (!fam) return null;

  const { data: kids, error: cErr } = await supabase
    .from("children")
    .select("id")
    .eq("family_id", fam.id);
  if (cErr) throw cErr;

  // 2. Load both type sets + all docs concurrently.
  const childIds = (kids || []).map((k) => k.id);
  const [fTypes, cTypes, fDocs, cDocRows] = await Promise.all([
    listDocumentTypes({ organizationId: fam.organization_id, scope: "family" }),
    listDocumentTypes({ organizationId: fam.organization_id, scope: "child" }),
    listFamilyDocuments(fam.id),
    childIds.length === 0
      ? []
      : supabase
          .from("child_documents")
          .select(
            "id, child_id, document_type_id, status, expires_at, signed_at, created_at",
          )
          .in("child_id", childIds)
          .then((r) => {
            if (r.error) throw r.error;
            return r.data || [];
          }),
  ]);

  // 3. Build checklist rows for each scope.
  const familyRows = buildDocumentChecklistRows(fTypes, fDocs);

  // For child scope, build one synthetic row per (child, type) so each
  // required type is required *per child*.
  const childRowsByChildAndType = [];
  for (const kid of kids || []) {
    const docsForChild = cDocRows.filter((d) => d.child_id === kid.id);
    for (const t of cTypes) {
      const docsForType = docsForChild.filter(
        (d) => d.document_type_id === t.id,
      );
      const latest =
        docsForType.length > 0
          ? [...docsForType].sort((a, b) =>
              (b.created_at || "").localeCompare(a.created_at || ""),
            )[0]
          : null;
      childRowsByChildAndType.push({
        type: t,
        documents: docsForType,
        latest,
        effectiveStatus: latest ? latest.status || "uploaded" : "missing",
        expiryState: latest ? _expiryState(latest.expires_at) : null,
      });
    }
  }

  const allRows = [...familyRows, ...childRowsByChildAndType];
  const summary = summariseDocumentChecklist(allRows);
  return {
    familyId: fam.id,
    childCount: (kids || []).length,
    ...summary,
  };
}

function _expiryState(iso) {
  if (!iso) return null;
  const now = Date.now();
  const exp = new Date(iso).getTime();
  if (Number.isNaN(exp)) return null;
  const thirtyDays = 30 * 24 * 60 * 60 * 1000;
  if (exp < now) return "expired";
  if (exp - now < thirtyDays) return "expiring";
  return "ok";
}

/**
 * Derive the parent-facing compliance status label from a summary.
 * Returns { tone: 'ok'|'warning'|'alert', label, hint, focus }
 *   focus is the URL query to pass when linking to /parent/documents so
 *   the page can scroll to the first relevant row.
 */
export function deriveComplianceStatus(summary) {
  if (!summary) return null;
  const { missingRequired, rejectedCount, expiredRequired, expiringRequired, uploadedCount } = summary;
  if (expiredRequired > 0 || rejectedCount > 0) {
    return {
      tone: "alert",
      label: "Action needed",
      hint:
        expiredRequired > 0 && rejectedCount > 0
          ? `${rejectedCount} rejected · ${expiredRequired} expired`
          : rejectedCount > 0
            ? `${rejectedCount} item${rejectedCount === 1 ? "" : "s"} rejected`
            : `${expiredRequired} item${expiredRequired === 1 ? "" : "s"} expired`,
      focus: rejectedCount > 0 ? "rejected" : "expired",
    };
  }
  if (missingRequired > 0) {
    return {
      tone: "alert",
      label: "Action needed",
      hint: `${missingRequired} missing item${missingRequired === 1 ? "" : "s"}`,
      focus: "missing",
    };
  }
  if (expiringRequired > 0) {
    return {
      tone: "warning",
      label: "Expiring soon",
      hint: `${expiringRequired} item${expiringRequired === 1 ? "" : "s"} renew within 30 days`,
      focus: "expiring",
    };
  }
  if (uploadedCount > 0) {
    return {
      tone: "warning",
      label: "In review",
      hint: `${uploadedCount} pending review`,
      focus: "uploaded",
    };
  }
  return {
    tone: "ok",
    label: "All set",
    hint: "Everything your center needs is on file",
    focus: null,
  };
}

